import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createAdminUser } from '../api';
import Button from './ui/Button';

const initialFormState = {
  userName: '',
  mail: '',
  firstName: '',
  lastName: '',
  password: '',
  groupId: '',
};

// `requireGroup`: a department administrator's new account must start in one of
// their groups, or it would belong to no department and vanish from their list.
function AdminCreateUserForm({ groups = [], requireGroup = false, onCreated }) {
  const { t } = useTranslation();

  const [form, setForm] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  function updateField(field) {
    return (event) => {
      setForm((current) => ({
        ...current,
        [field]: event.target.value,
      }));

      setError('');
    };
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const userName = form.userName.trim();
    const mail = form.mail.trim();
    const password = form.password;

    if (userName.length < 3) {
      setError(t('adminPanel.errorUsernameLength'));
      return;
    }

    if (mail && !/^\S+@\S+\.\S+$/.test(mail)) {
      setError(t('adminPanel.errorInvalidEmail'));
      return;
    }

    if (password.length < 8) {
      setError(t('adminPanel.errorPasswordLength'));
      return;
    }

    if (requireGroup && form.groupId === '') {
      setError(t('adminPanel.errorGroupRequired'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      await createAdminUser({
        user_name: userName,
        mail: mail || undefined,
        first_name: form.firstName.trim() || undefined,
        last_name: form.lastName.trim() || undefined,
        password,
        // The group says both what the person may do and where; anything more
        // is added below, in the block that lists every group.
        group_id:
          form.groupId === '' ? undefined : Number(form.groupId),
      });

      setForm(initialFormState);
      onCreated(t('adminPanel.createUserSuccess'));
    } catch (submitError) {
      console.error(
        'Failed to create user:',
        submitError,
      );

      setError(
        submitError.message ||
          t('adminPanel.createUserErrorGeneric'),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form
      className="account-form"
      onSubmit={handleSubmit}
      noValidate
    >
      {error && (
        <div
          className="login-message login-message-error"
          role="alert"
        >
          <i className="fa-solid fa-circle-exclamation" />
          <span>{error}</span>
        </div>
      )}

      <div className="account-field-row">
        <label htmlFor="adminNewUserName">
          {t('adminPanel.usernameLabel')}
        </label>

        <input
          id="adminNewUserName"
          name="userName"
          type="text"
          value={form.userName}
          disabled={isSubmitting}
          onChange={updateField('userName')}
        />
      </div>

      <div className="account-field-row">
        <label htmlFor="adminNewUserMail">
          {t('adminPanel.emailLabel')}
        </label>

        <input
          id="adminNewUserMail"
          name="mail"
          type="email"
          value={form.mail}
          disabled={isSubmitting}
          onChange={updateField('mail')}
        />
      </div>

      <div className="account-field-row">
        <label htmlFor="adminNewUserFirstName">
          {t('adminPanel.firstNameLabel')}
        </label>

        <input
          id="adminNewUserFirstName"
          name="firstName"
          type="text"
          value={form.firstName}
          disabled={isSubmitting}
          onChange={updateField('firstName')}
        />
      </div>

      <div className="account-field-row">
        <label htmlFor="adminNewUserLastName">
          {t('adminPanel.lastNameLabel')}
        </label>

        <input
          id="adminNewUserLastName"
          name="lastName"
          type="text"
          value={form.lastName}
          disabled={isSubmitting}
          onChange={updateField('lastName')}
        />
      </div>

      <div className="account-field-row">
        <label htmlFor="adminNewUserPassword">
          {t('adminPanel.passwordLabel')}
        </label>

        <input
          id="adminNewUserPassword"
          name="password"
          type="password"
          autoComplete="new-password"
          value={form.password}
          disabled={isSubmitting}
          onChange={updateField('password')}
        />
      </div>

      <div className="account-field-row">
        <label htmlFor="adminNewUserGroup">
          {t('adminPanel.groupLabel')}
        </label>

        <select
          id="adminNewUserGroup"
          name="groupId"
          value={form.groupId}
          disabled={isSubmitting}
          onChange={updateField('groupId')}
        >
          <option value="" disabled={requireGroup}>
            {requireGroup
              ? t('adminPanel.chooseGroupOption')
              : t('adminPanel.noGroupOption')}
          </option>

          {groups.map((group) => (
            <option key={group.group_id} value={group.group_id}>
              {group.is_platform
                ? group.group_name
                : `${group.group_name} — ${group.department_name}`}
            </option>
          ))}
        </select>
      </div>

      <p className="admin-user-section-hint">
        {t('adminPanel.createUserRightsHint')}
      </p>

      <div className="account-actions">
        <Button
          variant="primary"
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting
            ? t('adminPanel.creatingUser')
            : t('adminPanel.createUserButton')}
        </Button>
      </div>
    </form>
  );
}

export default AdminCreateUserForm;
